// src/pages/SearchPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api"; // axios instance — honors setApiLang etc.
import ArticleList from "../components/ArticleList";
import CategoryList from "../components/CategoryList";
import TagList from "../components/TagList";
import { useTranslation } from "../i18n";
import { Helmet } from "react-helmet-async";

/**
 * Modern Search Page
 * - sync with URL: ?query=...&category=...&tag=...&page=...
 * - debounce user typing, show suggestions (typeahead)
 * - respects language by sending 'Accept-Language' header and preferring translated fields
 */

const PAGE_SIZE = 12;
const SUGGESTIONS_LIMIT = 6;
const DEBOUNCE_MS = 400;

function useDebounced(value, delay = DEBOUNCE_MS) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function chooseTranslated(item, field, lang) {
  if (!item) return null;
  // 1) direct translations map (serializer in backend sets translations -> array/object)
  if (item.translations && typeof item.translations === "object") {
    // translations may be list or mapping; try mapping first
    if (Array.isArray(item.translations)) {
      const found = item.translations.find((tr) => String(tr.target_lang).startsWith(lang));
      if (found && found[field]) return found[field];
    } else if (item.translations[lang] && item.translations[lang][field]) {
      return item.translations[lang][field];
    } else {
      // fallback: find any translations entry with matching target_lang prefix
      for (const k of Object.keys(item.translations || {})) {
        if (String(k).startsWith(lang) && item.translations[k] && item.translations[k][field]) {
          return item.translations[k][field];
        }
      }
    }
  }

  // 2) suffix pattern: title_fr, excerpt_ar
  const suff = `${field}_${lang}`;
  if (suff in item && item[suff]) return item[suff];

  // 3) i18n / trans nested object
  if (item.i18n && item.i18n[lang] && item.i18n[lang][field]) return item.i18n[lang][field];
  if (item.trans && item.trans[lang] && item.trans[lang][field]) return item.trans[lang][field];

  // 4) top-level fallback
  return item[field] ?? null;
}

function highlight(text = "", query = "") {
  if (!text || !query) return text;
  try {
    const re = new RegExp(`(${String(query).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
    const parts = String(text).split(re);
    return parts.map((p, i) =>
      re.test(p) ? (
        <mark key={i} style={{ background: "#fff176", padding: "0 .12rem", borderRadius: 2 }}>
          {p}
        </mark>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  } catch {
    return text;
  }
}

export default function SearchPage() {
  const { lang: urlLang } = useParams(); // optional language prefix in route
  const { lang: i18nLang, t, setLang } = useTranslation(); // assume provider exists
  const lang = urlLang || i18nLang || "en";

  // sync i18n provider with URL lang (best-effort)
  useEffect(() => {
    if (urlLang && urlLang !== i18nLang) setLang(urlLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLang]);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL-driven state
  const initialQuery = searchParams.get("query") || "";
  const initialCategory = searchParams.get("category") || "";
  const initialTag = searchParams.get("tag") || "";
  const initialPage = Number(searchParams.get("page") || "1");

  const [query, setQuery] = useState(initialQuery);
  const debouncedQuery = useDebounced(query, DEBOUNCE_MS);
  const [category, setCategory] = useState(initialCategory);
  const [tag, setTag] = useState(initialTag);
  const [page, setPage] = useState(initialPage);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  const [articles, setArticles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cats, setCats] = useState([]);
  const [tags, setTags] = useState([]);
  const [error, setError] = useState(null);

  // update URL whenever filter/search/page changes (keeps shareable)
  useEffect(() => {
    const params = {};
    if (query) params.query = query;
    if (category) params.category = category;
    if (tag) params.tag = tag;
    if (page && page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [query, category, tag, page, setSearchParams]);

  // fetch categories & tags once (language-aware)
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const [cRes, tRes] = await Promise.all([
          api.get("/categories/", { signal: ctrl.signal, headers: { "Accept-Language": lang } }),
          api.get("/tags/", { signal: ctrl.signal, headers: { "Accept-Language": lang } }),
        ]);
        if (cancelled) return;
        setCats(cRes.data?.results || cRes.data || []);
        setTags(tRes.data?.results || tRes.data || []);
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        console.error("categories/tags fetch error", err);
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [lang]);

  // suggestions (typeahead) — show small list as user types
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await api.get("/articles/", {
          params: { search: debouncedQuery, page_size: SUGGESTIONS_LIMIT, ordering: "-created_at" },
          signal: ctrl.signal,
          headers: { "Accept-Language": lang },
        });
        if (cancelled) return;
        const items = res.data?.results || res.data || [];
        setSuggestions(items.slice(0, SUGGESTIONS_LIMIT));
        setShowSuggestions(true);
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        console.error("suggestions error", err);
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [debouncedQuery, lang]);

  // perform search (main results) — triggered by debouncedQuery, category, tag, page
  useEffect(() => {
    // don't search if empty query; allow searching by category/tag though (optional)
    const shouldSearch = Boolean(debouncedQuery) || Boolean(category) || Boolean(tag);
    if (!shouldSearch) {
      setArticles([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const ctrl = new AbortController();

    const fetch = async () => {
      try {
        setError(null);
        setLoading(true);
        const params = {
          search: debouncedQuery || undefined,
          category: category || undefined,
          tag: tag || undefined,
          page_size: PAGE_SIZE,
          page: page || 1,
          ordering: "-created_at",
        };
        const res = await api.get("/articles/", {
          params,
          signal: ctrl.signal,
          headers: { "Accept-Language": lang },
        });
        if (cancelled) return;
        const list = res.data?.results ?? (Array.isArray(res.data) ? res.data : []);
        const count = res.data?.count ?? (Array.isArray(res.data) ? list.length : 0);
        setArticles(list);
        setTotalCount(count);
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError(t("search_error") || "Search failed");
        console.error("search error", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [debouncedQuery, category, tag, page, lang, t]);

  // load more (append)
  const loadMore = useCallback(async () => {
    const nextPage = (page || 1) + 1;
    setLoadingMore(true);
    try {
      const res = await api.get("/articles/", {
        params: {
          search: debouncedQuery || undefined,
          category: category || undefined,
          tag: tag || undefined,
          page_size: PAGE_SIZE,
          page: nextPage,
          ordering: "-created_at",
        },
        headers: { "Accept-Language": lang },
      });
      const list = res.data?.results ?? (Array.isArray(res.data) ? res.data : []);
      setArticles((prev) => [...prev, ...list]);
      setPage(nextPage);
    } catch (err) {
      console.error("load more error", err);
    } finally {
      setLoadingMore(false);
    }
  }, [category, tag, debouncedQuery, page, lang]);

  // suggestions keyboard navigation
  useEffect(() => {
    let idx = -1;
    function onKey(e) {
      if (!showSuggestions || !suggestions.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        idx = Math.min(idx + 1, suggestions.length - 1);
        const el = suggestionsRef.current?.querySelectorAll("[data-sugg]")[idx];
        el?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        idx = Math.max(idx - 1, 0);
        const el = suggestionsRef.current?.querySelectorAll("[data-sugg]")[idx];
        el?.focus();
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSuggestions, suggestions]);

  // click outside suggestions -> hide
  useEffect(() => {
    function onDocClick(ev) {
      if (!suggestionsRef.current) return;
      if (!suggestionsRef.current.contains(ev.target) && ev.target !== inputRef.current) {
        setShowSuggestions(false);
      }
    }
    window.addEventListener("click", onDocClick);
    return () => window.removeEventListener("click", onDocClick);
  }, []);

  // helper to render list items with translated fields & highlight
  const renderedArticles = useMemo(() => {
    return (articles || []).map((a) => {
      const title = chooseTranslated(a, "title", lang) || a.title || "Untitled";
      const excerpt = chooseTranslated(a, "excerpt", lang) || a.excerpt || "";
      return { ...a, _title_translated: title, _excerpt_translated: excerpt };
    });
  }, [articles, lang]);

  // convenience: clear filters
  const clearFilters = () => {
    setQuery("");
    setCategory("");
    setTag("");
    setPage(1);
    setArticles([]);
    setTotalCount(0);
    // update URL (effect will run)
  };

  // clicking suggestion -> go to article page (preserve language path)
  const onPickSuggestion = (item) => {
    setShowSuggestions(false);
    const slug = item.slug || item.id;
    // navigate to localized article route: /:lang/articles/:slug
    navigate(`/${lang}/articles/${encodeURIComponent(slug)}`);
  };

  // small UI strings fallback via i18n
  const txt = (key, fallback) => {
    try {
      const v = t ? t(key) : null;
      return v || fallback || key;
    } catch {
      return fallback || key;
    }
  };

  return (
    <div className="container" style={{ maxWidth: 1200, padding: "28px 16px" }}>
      <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h1 className="mb-0" style={{ fontSize: 28 }}>{txt("search", "Search")}</h1>
          <div className="text-muted small">{txt("search_sub", "Find articles, topics and more")}</div>
        </div>
        <div className="text-end">
          <button className="btn btn-outline-secondary me-2" onClick={() => { setQuery(debouncedQuery); }}>
            {txt("refresh", "Refresh")}
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={clearFilters}>
            {txt("clear_filters", "Clear")}
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-4" role="search" aria-label={txt("search_form", "Search form")}>
        <div className="card-body">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-6 position-relative">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search" />
                </span>
                <input
                  ref={inputRef}
                  type="search"
                  className="form-control"
                  placeholder={txt("search_placeholder", "Search articles, e.g. climate, react")}
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                  onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
                  aria-autocomplete="list"
                  aria-controls="search-suggestions"
                  aria-expanded={showSuggestions}
                />
                {query && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => { setQuery(""); inputRef.current?.focus(); }}
                    aria-label={txt("clear_search", "Clear search")}
                    title={txt("clear_search", "Clear search")}
                  >
                    ✕
                  </button>
                )}
              </div>

              {showSuggestions && suggestions.length > 0 && (
                <div
                  id="search-suggestions"
                  ref={suggestionsRef}
                  className="list-group position-absolute w-100 shadow-sm"
                  style={{ zIndex: 1100, top: "calc(100% + 6px)" }}
                >
                  {suggestions.map((s, idx) => {
                    const title = chooseTranslated(s, "title", lang) || s.title || "";
                    return (
                      <button
                        key={s.id ?? s.slug ?? idx}
                        type="button"
                        data-sugg
                        className="list-group-item list-group-item-action d-flex gap-2 align-items-start"
                        onClick={() => onPickSuggestion(s)}
                        onKeyDown={(e) => { if (e.key === "Enter") onPickSuggestion(s); }}
                        title={title}
                      >
                        <div style={{ flex: "0 0 64px", width: 64, height: 48, overflow: "hidden", borderRadius: 6 }}>
                          <img
                            src={s.image || s.thumbnail || `https://picsum.photos/seed/${s.id || s.slug}/200/120`}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            loading="lazy"
                          />
                        </div>
                        <div className="text-start" style={{ flex: "1 1 auto", minWidth: 0 }}>
                          <div className="fw-semibold" style={{ fontSize: 14 }}>{highlight(title, debouncedQuery)}</div>
                          <div className="text-muted small" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {chooseTranslated(s, "excerpt", lang) || s.excerpt || ""}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className="list-group-item list-group-item-action small text-center text-muted"
                    onClick={() => {
                      setShowSuggestions(false);
                      // ensure results view updates
                    }}
                  >
                    {txt("see_all_results", "See all results")}
                  </button>
                </div>
              )}
            </div>

            <div className="col-12 col-md-3">
              <select
                className="form-select"
                value={category}
                onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                aria-label={txt("filter_category", "Filter by category")}
              >
                <option value="">{txt("all_categories", "All categories")}</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-3">
              <select
                className="form-select"
                value={tag}
                onChange={(e) => { setTag(e.target.value); setPage(1); }}
                aria-label={txt("filter_tag", "Filter by tag")}
              >
                <option value="">{txt("all_tags", "All tags")}</option>
                {tags.map((tg) => (
                  <option key={tg.id} value={tg.slug}>{tg.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* main content */}
      <div className="row g-4">
        <main className="col-lg-8">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              {loading ? (
                <div className="text-muted">{txt("searching", "Searching…")}</div>
              ) : (
                <div className="small text-muted">
                  {totalCount} {txt("results_for", "results for")} <strong>{debouncedQuery || (category || tag ? txt("filters", "filters") : "")}</strong>
                </div>
              )}
            </div>

            <div className="text-end">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => { setQuery(""); setCategory(""); setTag(""); setPage(1); }}
              >
                {txt("reset", "Reset")}
              </button>
            </div>
          </div>

          {/* results / skeleton */}
          {loading && (
            <>
              <div className="mb-3" style={{ height: 200 }}>
                {/* lightweight skeleton */}
                <div className="skeleton" style={{ height: 12, width: "40%", background: "#eee", marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 10, width: "70%", background: "#eee", marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 10, width: "60%", background: "#eee" }} />
              </div>
            </>
          )}

          {!loading && renderedArticles.length > 0 ? (
            <>
              {/* pass translated fields into ArticleList by converting objects */}
              <ArticleList
                articles={renderedArticles.map((a) => ({
                  ...a,
                  title: a._title_translated,
                  excerpt: a._excerpt_translated,
                }))}
              />

              {articles.length < totalCount && (
                <div className="text-center mt-3">
                  <button className="btn btn-outline-primary" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? txt("loading_more", "Loading…") : txt("load_more", "Load more")}
                  </button>
                </div>
              )}
            </>
          ) : null}

          {!loading && debouncedQuery && renderedArticles.length === 0 && (
            <div className="alert alert-light">{txt("no_results", "No results found")}</div>
          )}

          {!debouncedQuery && !category && !tag && (
            <div className="alert alert-info">
              {txt("search_prompt", "Start typing to search articles, or try a category/tag filter.")}
            </div>
          )}

        </main>

        <aside className="col-lg-4">
          <div className="card mb-3 shadow-sm">
            <div className="card-body">
              <h6 className="mb-3 fw-semibold">{txt("filters", "Filters")}</h6>
              <div className="mb-2">
                <div className="small text-muted mb-2">{txt("categories", "Categories")}</div>
                <CategoryList categories={cats} compact onPick={(c) => { setCategory(c.slug); setPage(1); }} />
              </div>
              <div>
                <div className="small text-muted mb-2">{txt("tags", "Tags")}</div>
                <TagList tags={tags} compact onPick={(tg) => { setTag(tg.slug); setPage(1); }} />
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="mb-2 fw-semibold">{txt("tips", "Search tips")}</h6>
              <ul className="small text-muted mb-0">
                <li>{txt("tip_exact", "Use quotes for exact phrases, e.g. \"climate change\"")}</li>
                <li>{txt("tip_filters", "Combine with category or tag filters for precise results")}</li>
                <li>{txt("tip_lang", "Results prefer translations for your chosen language")}</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        .skeleton { border-radius: 6px; background: linear-gradient(90deg,#eee,#f6f6f6,#eee); animation: pulse 1.2s infinite; }
        @keyframes pulse { 0% { opacity: 1 } 50% { opacity: 0.6 } 100% { opacity: 1 } }
        mark { background: #fff176; color: inherit; }
      `}</style>
    </div>
  );
}
